import Vue from 'vue'
import Vuex from 'vuex'
import Meta from 'vue-meta'
import ClientOnly from 'vue-client-only'
import NoSsr from 'vue-no-ssr'
import { createRouter } from './router.js'
import NuxtChild from './components/nuxt-child.js'
import NuxtError from './components/nuxt-error.vue'
import Nuxt from './components/nuxt.js'
import App from './App.js'
import { setContext, getLocation, getRouteData, normalizeError } from './utils'
import { createStore } from './store.js'

/* Plugins */

import nuxt_plugin_plugin_07c9c1c4 from 'nuxt_plugin_plugin_07c9c1c4' // Source: ./composition-api/plugin.mjs (mode: 'all')
import nuxt_plugin_axios_eff9da68 from 'nuxt_plugin_axios_eff9da68' // Source: ./axios.js (mode: 'all')
import nuxt_plugin_deviceplugin_401dc352 from 'nuxt_plugin_deviceplugin_401dc352' // Source: ./device.plugin.js (mode: 'all')
import nuxt_plugin_ttoauth_4ebddfac from 'nuxt_plugin_ttoauth_4ebddfac' // Source: ../plugins/ttoauth.ts (mode: 'all')
import nuxt_plugin_location_3bf17bbc from 'nuxt_plugin_location_3bf17bbc' // Source: ../plugins/location.ts (mode: 'server')
import nuxt_plugin_aos_78651c04 from 'nuxt_plugin_aos_78651c04' // Source: ../plugins/aos.ts (mode: 'client')
import nuxt_plugin_useinapp_7c099a98 from 'nuxt_plugin_useinapp_7c099a98' // Source: ../plugins/use-in-app.ts (mode: 'client')
import nuxt_plugin_i18n_1fba4fce from 'nuxt_plugin_i18n_1fba4fce' // Source: ../plugins/i18n.ts (mode: 'all')
import nuxt_plugin_vui_301991ba from 'nuxt_plugin_vui_301991ba' // Source: ../plugins/vui.js (mode: 'client')
import nuxt_plugin_abtest_0d28fb64 from 'nuxt_plugin_abtest_0d28fb64' // Source: ../plugins/abtest.ts (mode: 'server')
import nuxt_plugin_ga_34d435b2 from 'nuxt_plugin_ga_34d435b2' // Source: ../plugins/ga.js (mode: 'client')
import nuxt_plugin_slardar_6f40db02 from 'nuxt_plugin_slardar_6f40db02' // Source: ../plugins/slardar.js (mode: 'client')
import nuxt_plugin_renderer_606c5e54 from 'nuxt_plugin_renderer_606c5e54' // Source: ../plugins/renderer.ts (mode: 'all')
import nuxt_plugin_register_41f42474 from 'nuxt_plugin_register_41f42474' // Source: ../plugins/register.ts (mode: 'client')
import nuxt_plugin_attribution_08b7bb44 from 'nuxt_plugin_attribution_08b7bb44' // Source: ../plugins/attribution.ts (mode: 'client')
import nuxt_plugin_route_192d848c from 'nuxt_plugin_route_192d848c' // Source: ../plugins/route.ts (mode: 'client')
import nuxt_plugin_acrawler_5c0ae984 from 'nuxt_plugin_acrawler_5c0ae984' // Source: ../plugins/acrawler.ts (mode: 'client')
import nuxt_plugin_handshakeclient_2cf28b15 from 'nuxt_plugin_handshakeclient_2cf28b15' // Source: ../plugins/handshake.client.ts (mode: 'client')
import nuxt_plugin_cookieBanner_1e242c47 from 'nuxt_plugin_cookieBanner_1e242c47' // Source: ../plugins/cookieBanner.ts (mode: 'client')
import nuxt_plugin_ttwid_630e0bde from 'nuxt_plugin_ttwid_630e0bde' // Source: ../plugins/ttwid.ts (mode: 'client')
import nuxt_plugin_whitelistService_76e9fe6e from 'nuxt_plugin_whitelistService_76e9fe6e' // Source: ../plugins/whitelistService.ts (mode: 'client')
import nuxt_plugin_copilot_2ffd99d7 from 'nuxt_plugin_copilot_2ffd99d7' // Source: ../plugins/copilot.ts (mode: 'server')
import nuxt_plugin_contentful_0fea6ada from 'nuxt_plugin_contentful_0fea6ada' // Source: ../plugins/contentful.ts (mode: 'server')
import nuxt_plugin_meta_0ddc29b0 from 'nuxt_plugin_meta_0ddc29b0' // Source: ./composition-api/meta.mjs (mode: 'all')

// Component: <ClientOnly>
Vue.component(ClientOnly.name, ClientOnly)

// TODO: Remove in Nuxt 3: <NoSsr>
Vue.component(NoSsr.name, {
  ...NoSsr,
  render (h, ctx) {
    if (process.client && !NoSsr._warned) {
      NoSsr._warned = true

      console.warn('<no-ssr> has been deprecated and will be removed in Nuxt 3, please use <client-only> instead')
    }
    return NoSsr.render(h, ctx)
  }
})

// Component: <NuxtChild>
Vue.component(NuxtChild.name, NuxtChild)
Vue.component('NChild', NuxtChild)

// Component NuxtLink is imported in server.js or client.js

// Component: <Nuxt>
Vue.component(Nuxt.name, Nuxt)

Object.defineProperty(Vue.prototype, '$nuxt', {
  get() {
    const globalNuxt = this.$root.$options.$nuxt
    if (process.client && !globalNuxt && typeof window !== 'undefined') {
      return window.$nuxt
    }
    return globalNuxt
  },
  configurable: true
})

Vue.use(Meta, {"keyName":"head","attribute":"data-n-head","ssrAttribute":"data-n-head-ssr","tagIDKeyName":"hid"})

const defaultTransition = {"name":"page","mode":"out-in","appear":true,"appearClass":"appear","appearActiveClass":"appear-active","appearToClass":"appear-to"}

const originalRegisterModule = Vuex.Store.prototype.registerModule

function registerModule (path, rawModule, options = {}) {
  const preserveState = process.client && (
    Array.isArray(path)
      ? !!path.reduce((namespacedState, path) => namespacedState && namespacedState[path], this.state)
      : path in this.state
  )
  return originalRegisterModule.call(this, path, rawModule, { preserveState, ...options })
}

async function createApp(ssrContext, config = {}) {
  const router = await createRouter(ssrContext, config)

  const store = createStore(ssrContext)
  // Add this.$router into store actions/mutations
  store.$router = router

  // Create Root instance

  // here we inject the router and store to all child components,
  // making them available everywhere as `this.$router` and `this.$store`.
  const app = {
    head: {"title":"Get your business discovered on TikTok","meta":[{"charset":"utf-8"},{"name":"viewport","content":"width=device-width, initial-scale=1, maximum-scale=1.0, user-scalable=no"},{"hid":"description","name":"description","content":"Sign up TikTok For Business now. Start advertising on TikTok today to drive real business results."},{"name":"referrer","content":"always"},{"hid":"og:url","property":"og:url","content":"https:\u002F\u002Fgetstarted.tiktok.com"},{"hid":"og:title","property":"og:title","content":"Get your business discovered on TikTok"},{"hid":"og:description","property":"og:description","content":"Sign up TikTok For Business now. Start advertising on TikTok today to drive real business results."},{"hid":"og:image","property":"og:image","content":"https:\u002F\u002Fgetstarted.tiktok.com\u002Flogo-big.png"},{"hid":"og:type","property":"og:type","content":"website"},{"property":"google-site-verification","content":"ePJ3QjccFc-h3IPaKquaXl7ArSS4j7bCL_Q6R7OYABI"},{"property":"facebook-domain-verification","content":"iec6txjjw8r35xhq5swdem8tpa5mzw"}],"link":[{"rel":"stylesheet","type":"text\u002Fcss","href":"https:\u002F\u002Fsf16-scmcdn-sg.ibytedtos.com\u002Fobj\u002Fgoofy-sg\u002Ftt4b_main\u002Fcommon-style\u002Fv1.css"},{"rel":"preconnect","href":"\u002F\u002Fsf16-website.neutral.ttwstatic.com"},{"rel":"preconnect","href":"\u002F\u002Fsf16-scmcdn-sg.ibytedtos.com"},{"rel":"preconnect","href":"\u002F\u002Flf16-adcdn-va.ibytedtos.com"},{"rel":"preconnect","href":"\u002F\u002Fbusiness-sso.tiktok.com"},{"rel":"icon","type":"image\u002Fx-icon","href":"https:\u002F\u002Fp16-tt4b-cms-va.ibyteimg.com\u002Ftos-maliva-i-wnrj55q040-us\u002Ffavicon.ico~tplv-wnrj55q040-png-75:111:111.png"},{"rel":"apple-touch-icon","sizes":"120x120","href":"https:\u002F\u002Fp16-tt4b-cms-va.ibyteimg.com\u002Ftos-maliva-i-wnrj55q040-us\u002F20221116-104248.png~tplv-wnrj55q040-png-75:1111:1111.png"},{"rel":"apple-touch-icon-precomposed","sizes":"120x120","href":"https:\u002F\u002Fp16-tt4b-cms-va.ibyteimg.com\u002Ftos-maliva-i-wnrj55q040-us\u002F20221116-104248.png~tplv-wnrj55q040-png-75:1111:1111.png"}],"style":[],"script":[]},

    store,
    router,
    nuxt: {
      defaultTransition,
      transitions: [defaultTransition],
      setTransitions (transitions) {
        if (!Array.isArray(transitions)) {
          transitions = [transitions]
        }
        transitions = transitions.map((transition) => {
          if (!transition) {
            transition = defaultTransition
          } else if (typeof transition === 'string') {
            transition = Object.assign({}, defaultTransition, { name: transition })
          } else {
            transition = Object.assign({}, defaultTransition, transition)
          }
          return transition
        })
        this.$options.nuxt.transitions = transitions
        return transitions
      },

      err: null,
      dateErr: null,
      error (err) {
        err = err || null
        app.context._errored = Boolean(err)
        err = err ? normalizeError(err) : null
        let nuxt = app.nuxt // to work with @vue/composition-api, see https://github.com/nuxt/nuxt.js/issues/6517#issuecomment-573280207
        if (this) {
          nuxt = this.nuxt || this.$options.nuxt
        }
        nuxt.dateErr = Date.now()
        nuxt.err = err
        // Used in src/server.js
        if (ssrContext) {
          ssrContext.nuxt.error = err
        }
        return err
      }
    },
    ...App
  }

  // Make app available into store via this.app
  store.app = app

  const next = ssrContext ? ssrContext.next : location => app.router.push(location)
  // Resolve route
  let route
  if (ssrContext) {
    route = router.resolve(ssrContext.url).route
  } else {
    const path = getLocation(router.options.base, router.options.mode)
    route = router.resolve(path).route
  }

  // Set context to app.context
  await setContext(app, {
    store,
    route,
    next,
    error: app.nuxt.error.bind(app),
    payload: ssrContext ? ssrContext.payload : undefined,
    req: ssrContext ? ssrContext.req : undefined,
    res: ssrContext ? ssrContext.res : undefined,
    beforeRenderFns: ssrContext ? ssrContext.beforeRenderFns : undefined,
    ssrContext
  })

  function inject(key, value) {
    if (!key) {
      throw new Error('inject(key, value) has no key provided')
    }
    if (value === undefined) {
      throw new Error(`inject('${key}', value) has no value provided`)
    }

    key = '$' + key
    // Add into app
    app[key] = value
    // Add into context
    if (!app.context[key]) {
      app.context[key] = value
    }

    // Add into store
    store[key] = app[key]

    // Check if plugin not already installed
    const installKey = '__nuxt_' + key + '_installed__'
    if (Vue[installKey]) {
      return
    }
    Vue[installKey] = true
    // Call Vue.use() to install the plugin into vm
    Vue.use(() => {
      if (!Object.prototype.hasOwnProperty.call(Vue.prototype, key)) {
        Object.defineProperty(Vue.prototype, key, {
          get () {
            return this.$root.$options[key]
          }
        })
      }
    })
  }

  // Inject runtime config as $config
  inject('config', config)

  if (process.client) {
    // Replace store state before plugins execution
    if (window.__NUXT__ && window.__NUXT__.state) {
      store.replaceState(window.__NUXT__.state)
    }
  }

  // Add enablePreview(previewData = {}) in context for plugins
  if (process.static && process.client) {
    app.context.enablePreview = function (previewData = {}) {
      app.previewData = Object.assign({}, previewData)
      inject('preview', previewData)
    }
  }
  // Plugin execution

  if (typeof nuxt_plugin_plugin_07c9c1c4 === 'function') {
    await nuxt_plugin_plugin_07c9c1c4(app.context, inject)
  }

  if (typeof nuxt_plugin_axios_eff9da68 === 'function') {
    await nuxt_plugin_axios_eff9da68(app.context, inject)
  }

  if (typeof nuxt_plugin_deviceplugin_401dc352 === 'function') {
    await nuxt_plugin_deviceplugin_401dc352(app.context, inject)
  }

  if (typeof nuxt_plugin_ttoauth_4ebddfac === 'function') {
    await nuxt_plugin_ttoauth_4ebddfac(app.context, inject)
  }

  if (process.server && typeof nuxt_plugin_location_3bf17bbc === 'function') {
    await nuxt_plugin_location_3bf17bbc(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_aos_78651c04 === 'function') {
    await nuxt_plugin_aos_78651c04(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_useinapp_7c099a98 === 'function') {
    await nuxt_plugin_useinapp_7c099a98(app.context, inject)
  }

  if (typeof nuxt_plugin_i18n_1fba4fce === 'function') {
    await nuxt_plugin_i18n_1fba4fce(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vui_301991ba === 'function') {
    await nuxt_plugin_vui_301991ba(app.context, inject)
  }

  if (process.server && typeof nuxt_plugin_abtest_0d28fb64 === 'function') {
    await nuxt_plugin_abtest_0d28fb64(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_ga_34d435b2 === 'function') {
    await nuxt_plugin_ga_34d435b2(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_slardar_6f40db02 === 'function') {
    await nuxt_plugin_slardar_6f40db02(app.context, inject)
  }

  if (typeof nuxt_plugin_renderer_606c5e54 === 'function') {
    await nuxt_plugin_renderer_606c5e54(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_register_41f42474 === 'function') {
    await nuxt_plugin_register_41f42474(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_attribution_08b7bb44 === 'function') {
    await nuxt_plugin_attribution_08b7bb44(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_route_192d848c === 'function') {
    await nuxt_plugin_route_192d848c(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_acrawler_5c0ae984 === 'function') {
    await nuxt_plugin_acrawler_5c0ae984(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_handshakeclient_2cf28b15 === 'function') {
    await nuxt_plugin_handshakeclient_2cf28b15(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_cookieBanner_1e242c47 === 'function') {
    await nuxt_plugin_cookieBanner_1e242c47(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_ttwid_630e0bde === 'function') {
    await nuxt_plugin_ttwid_630e0bde(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_whitelistService_76e9fe6e === 'function') {
    await nuxt_plugin_whitelistService_76e9fe6e(app.context, inject)
  }

  if (process.server && typeof nuxt_plugin_copilot_2ffd99d7 === 'function') {
    await nuxt_plugin_copilot_2ffd99d7(app.context, inject)
  }

  if (process.server && typeof nuxt_plugin_contentful_0fea6ada === 'function') {
    await nuxt_plugin_contentful_0fea6ada(app.context, inject)
  }

  if (typeof nuxt_plugin_meta_0ddc29b0 === 'function') {
    await nuxt_plugin_meta_0ddc29b0(app.context, inject)
  }

  // Lock enablePreview in context
  if (process.static && process.client) {
    app.context.enablePreview = function () {
      console.warn('You cannot call enablePreview() outside a plugin.')
    }
  }

  // Wait for async component to be resolved first
  await new Promise((resolve, reject) => {
    // Ignore 404s rather than blindly replacing URL in browser
    if (process.client) {
      const { route } = router.resolve(app.context.route.fullPath)
      if (!route.matched.length) {
        return resolve()
      }
    }
    router.replace(app.context.route.fullPath, resolve, (err) => {
      // https://github.com/vuejs/vue-router/blob/v3.4.3/src/util/errors.js
      if (!err._isRouter) return reject(err)
      if (err.type !== 2 /* NavigationFailureType.redirected */) return resolve()

      // navigated to a different route in router guard
      const unregister = router.afterEach(async (to, from) => {
        if (process.server && ssrContext && ssrContext.url) {
          ssrContext.url = to.fullPath
        }
        app.context.route = await getRouteData(to)
        app.context.params = to.params || {}
        app.context.query = to.query || {}
        unregister()
        resolve()
      })
    })
  })

  return {
    store,
    app,
    router
  }
}

export { createApp, NuxtError }
